import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatMoney } from '../lib/format';
import type { AccountType } from '../lib/types';

interface BalanceRow {
  account_id: string;
  code: string;
  name: string;
  account_type: AccountType;
  total_debit: number;
  total_credit: number;
  balance: number;
}

const TYPE_LABEL: Record<AccountType, string> = {
  asset: 'Assets',
  liability: 'Liabilities',
  equity: 'Equity',
  revenue: 'Revenue',
  cogs: 'Cost of sales',
  expense: 'Expenses',
  contra: 'Contra-asset',
};
const TYPE_ORDER: AccountType[] = ['asset', 'contra', 'liability', 'equity', 'revenue', 'cogs', 'expense'];

export default function ChartOfAccounts() {
  const { currentOrg } = useOrg();
  const [rows, setRows] = useState<BalanceRow[]>([]);

  useEffect(() => {
    if (!currentOrg) return;
    supabase
      .from('dodobooks_account_balances')
      .select('*')
      .eq('org_id', currentOrg.id)
      .order('code')
      .then(({ data }) => setRows((data ?? []) as BalanceRow[]));
  }, [currentOrg]);

  const grouped = TYPE_ORDER.map((t) => ({ type: t, rows: rows.filter((r) => r.account_type === t) })).filter((g) => g.rows.length > 0);

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="h-page">Chart of accounts</h1>
        <p className="text-text-dim text-sm mt-1">SA-default ledger structure — 34 accounts seeded when this org was created.</p>
      </header>

      {grouped.map((g) => (
        <div key={g.type} className="card p-0">
          <div className="px-5 py-3 border-b border-line">
            <h2 className="h-section">{TYPE_LABEL[g.type]}</h2>
          </div>
          <table>
            <thead>
              <tr>
                <th className="px-5">Code</th>
                <th>Name</th>
                <th className="text-right">Debit</th>
                <th className="text-right">Credit</th>
                <th className="text-right pr-5">Balance</th>
              </tr>
            </thead>
            <tbody>
              {g.rows.map((r) => (
                <tr key={r.account_id}>
                  <td className="px-5 font-mono text-text-dim">{r.code}</td>
                  <td>{r.name}</td>
                  <td className="text-right num text-text-dim">{formatMoney(r.total_debit)}</td>
                  <td className="text-right num text-text-dim">{formatMoney(r.total_credit)}</td>
                  <td className="text-right num pr-5">{formatMoney(r.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
